import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import Icon from '../../components/common/Icon';
import Button from '../../components/common/Button';
import { orderAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import RefundDeadlineBadge from '../../components/common/RefundDeadlineBadge';
import { isRefundPending } from '../../utils/refundTimeline';
import { formatDate } from '../../utils/dateTime';

const ORDER_STATUS = {
  ORDER_FORM_FILLED: 'ORDER_FORM_FILLED',
  REFUND_FORM_FILLED: 'REFUND_FORM_FILLED',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  REJECT_AND_REFILL: 'REJECT_AND_REFILL',
  ORDER_FORM_REFILL_REQUEST: 'ORDER_FORM_REFILL_REQUEST',
  REFUND_FORM_REFILL_REQUEST: 'REFUND_FORM_REFILL_REQUEST',
  PAID: 'PAID',
  PAID_TO_MEDIATOR: 'PAID_TO_MEDIATOR',
  PAID_TO_BUYER: 'PAID_TO_BUYER'
};

const formatOrderTitle = (order) => {
  const brand = order.brandName?.trim();
  const product = order.productName?.trim() || 'Product';
  return brand ? `${brand}: ${product}` : product;
};

const getDisplayStatus = (status) => {
  // PAID and PAID_TO_MEDIATOR should be displayed as 'Approved'
  if ([ORDER_STATUS.APPROVED, ORDER_STATUS.PAID, ORDER_STATUS.PAID_TO_MEDIATOR].includes(status)) 
    return { label: 'Approved', color: '#10b981', bg: '#d1fae5' };
  
  // Only PAID_TO_BUYER should be shown as 'Paid'
  if (status === ORDER_STATUS.PAID_TO_BUYER) 
    return { label: 'Paid', color: '#059669', bg: '#ecfdf5' };
    
  if (status === ORDER_STATUS.REJECTED) 
    return { label: 'Rejected', color: '#ef4444', bg: '#fee2e2' };
    
  if ([ORDER_STATUS.ORDER_FORM_REFILL_REQUEST, ORDER_STATUS.REFUND_FORM_REFILL_REQUEST, ORDER_STATUS.REJECT_AND_REFILL].includes(status))
    return { label: status === ORDER_STATUS.REJECT_AND_REFILL ? 'Reject and Refill' : 'Refill Required', color: '#8b5cf6', bg: '#f3e8ff' };
    
  return { label: status?.replace(/_/g, ' ') || 'Pending', color: '#64748b', bg: '#f1f5f9' };
};

const BuyerOrders = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);

  const fetchOrders = async () => {
    // Try localStorage if context email is not ready
    const userEmail = user?.email || JSON.parse(localStorage.getItem('user') || '{}')?.email;
    if (!userEmail) {
      console.log('Orders Page: No user email found');
      return;
    }
    
    setLoading(true);
    try {
      const params = {
        page: 0,
        size: 50,
        sortBy: 'creationDate',
        sortDirection: 'DESC',
        userEmail: userEmail
      };
      console.log('Orders Page: Fetching with email:', userEmail);
      const response = await orderAPI.searchOrders(params);
      console.log('Orders Page: Raw response:', response);
      
      const data = response.data;
      let ordersArray = [];
      
      if (data && data.data && Array.isArray(data.data)) {
        ordersArray = data.data;
      } else if (data && Array.isArray(data.content)) {
        // Handle Spring Page object structure
        ordersArray = data.content;
      } else if (Array.isArray(data)) {
        ordersArray = data;
      } else if (data && typeof data === 'object') {
        const possibleArray = Object.values(data).find(val => Array.isArray(val));
        if (possibleArray) ordersArray = possibleArray;
      }
      
      console.log('Orders Page: Processed orders:', ordersArray);
      setOrders(ordersArray);
    } catch (error) {
      console.error('Orders Page: Failed to fetch orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [user?.email]);

  const getStatusBadge = (status) => {
    // Mirror Dashboard masking logic for consistency
    const displayStatus = getDisplayStatus(status);
    return { 
      color: displayStatus.color, 
      bg: displayStatus.bg, 
      label: displayStatus.label 
    };
  };

  const handleRefillOrder = (order) => {
    navigate('/order', { 
      state: { 
        orderId: order.orderId,
        isEdit: true,
        orderData: order 
      } 
    });
  };

  const handleRefillRefund = (order) => {
    navigate(`/refund?edit=${order.id}&refill=true&orderId=${encodeURIComponent(order.orderId)}`, { 
      state: { 
        orderId: order.orderId,
        isEdit: true,
        orderData: order 
      } 
    });
  };

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setShowOrderModal(true);
  };

  return (
    <Layout title="My Orders">
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 24px' }}>
        <div style={{
          background: 'var(--bg-card)',
          borderRadius: '12px',
          border: '1px solid var(--border)',
          overflow: 'hidden'
        }}>
          <div style={{ padding: '24px', borderBottom: '1px solid var(--border)' }}>
            <h2 style={{ 
              fontFamily: 'Outfit, sans-serif',
              fontWeight: '700', 
              fontSize: '20px', 
              color: 'var(--text)'
            }}>
              Order History
            </h2>
            <p style={{ fontSize: '14px', color: 'var(--text-muted)', marginTop: '4px' }}>
              Track and manage all your orders
            </p>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: 'var(--bg-deep)' }}>
                <tr>
                  <th style={{ padding: '16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Order ID</th>
                  <th style={{ padding: '16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Product</th>
                  <th style={{ padding: '16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Date</th>
                  <th style={{ padding: '16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Refund Filled</th>
                  <th style={{ padding: '16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Amount</th>
                  <th style={{ padding: '16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Status</th>
                  <th style={{ padding: '16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" style={{ padding: '48px', textAlign: 'center' }}>
                      <div style={{ display: 'inline-block', width: '24px', height: '24px', border: '2px solid var(--border)', borderTopColor: 'var(--primary)', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                    </td>
                  </tr>
                ) : orders.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={{ padding: '48px', textAlign: 'center', color: 'var(--text-muted)' }}>
                      No orders found.
                    </td>
                  </tr>
                ) : (
                  orders.map((order) => {
                    const status = getStatusBadge(order.orderStatus);
                    return (
                      <tr key={order.id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '16px', fontSize: '14px', fontWeight: '500' }}>{order.orderId}</td>
                        <td style={{ padding: '16px', fontSize: '14px' }}>{formatOrderTitle(order)}</td>
                        <td style={{ padding: '16px', fontSize: '14px', color: 'var(--text-muted)' }}>
                          {formatDate(order.orderDate)}
                        </td>
                        <td style={{ padding: '16px', fontSize: '14px', color: 'var(--text-muted)' }}>
                          {order.refundFormFilledDate ? formatDate(order.refundFormFilledDate) : '—'}
                        </td>
                        <td style={{ padding: '16px', fontSize: '14px', fontWeight: '600', color: 'var(--primary)' }}>
                          ₹{order.orderAmount}
                        </td>
                        <td style={{ padding: '16px' }}>
                          <span style={{
                            padding: '4px 10px',
                            borderRadius: '99px',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: status.color,
                            background: status.bg
                          }}>
                            {status.label}
                          </span>
                          {isRefundPending(order.orderStatus) && order.refundTimeline && (
                            <div style={{ marginTop: '8px' }}>
                              <RefundDeadlineBadge timeline={order.refundTimeline} />
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '16px', textAlign: 'right', display: 'flex', gap: '8px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                          {isRefundPending(order.orderStatus) && order.refundTimeline?.refundFormOpen && (
                            <Button
                              size="sm"
                              onClick={() => navigate(`/refund?orderId=${encodeURIComponent(order.orderId)}`, { state: { orderId: order.orderId, orderData: order } })}
                            >
                              Fill Refund Form
                            </Button>
                          )}
                          <Button 
                            variant="secondary" 
                            size="sm"
                            onClick={() => handleViewDetails(order)}
                          >
                            View
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showOrderModal && selectedOrder && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ background: '#ffffff', borderRadius: '16px', width: '100%', maxWidth: '600px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700' }}>Order Details</h3>
              <button onClick={() => setShowOrderModal(false)} style={{ background: '#f3f4f6', border: 'none', borderRadius: '50%', width: '32px', height: '32px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon name="x" size={20} />
              </button>
            </div>
            <div style={{ padding: '24px', overflowY: 'auto' }}>
              <div style={{ display: 'grid', gap: '20px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <p style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Order ID</p>
                    <p style={{ fontSize: '14px', fontWeight: '600' }}>{selectedOrder.orderId}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Date</p>
                    <p style={{ fontSize: '14px', fontWeight: '600' }}>{formatDate(selectedOrder.orderDate)}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Refund Filled</p>
                    <p style={{ fontSize: '14px', fontWeight: '600' }}>{selectedOrder.refundFormFilledDate ? formatDate(selectedOrder.refundFormFilledDate) : '—'}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Product</p>
                    <p style={{ fontSize: '14px', fontWeight: '600' }}>{selectedOrder.productName}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Amount</p>
                    <p style={{ fontSize: '14px', fontWeight: '600', color: 'var(--primary)' }}>₹{selectedOrder.orderAmount}</p>
                  </div>
                </div>

                <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '12px' }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600' }}>Status Information</h4>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '13px', color: '#64748b' }}>Current Status</span>
                    <span style={{
                      padding: '4px 10px',
                      borderRadius: '99px',
                      fontSize: '12px',
                      fontWeight: '600',
                      color: getStatusBadge(selectedOrder.orderStatus).color,
                      background: getStatusBadge(selectedOrder.orderStatus).bg
                    }}>
                      {getStatusBadge(selectedOrder.orderStatus).label}
                    </span>
                  </div>
                </div>

                {(selectedOrder.orderStatus === 'ORDER_FORM_REFILL_REQUEST'
                  || selectedOrder.orderStatus === 'REFUND_FORM_REFILL_REQUEST'
                  || selectedOrder.orderStatus === 'REJECT_AND_REFILL'
                  || selectedOrder.reason) && (
                  <div style={{ padding: '16px', background: '#fef2f2', borderRadius: '12px' }}>
                    <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600', color: '#b91c1c' }}>
                      {selectedOrder.orderStatus?.includes('REFILL') ? 'Refill Reason' : 'Reason for Rejection'}
                    </h4>
                    <p style={{ fontSize: '13px', color: '#b91c1c' }}>{selectedOrder.reason || 'Please check with your mediator'}</p>
                  </div>
                )}
              </div>
            </div>
            <div style={{ padding: '16px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              {selectedOrder.orderStatus === 'ORDER_FORM_REFILL_REQUEST' && (
                <Button variant="primary" onClick={() => handleRefillOrder(selectedOrder)}>Refill Order Form</Button>
              )}
              {(selectedOrder.orderStatus === 'REFUND_FORM_REFILL_REQUEST' || selectedOrder.orderStatus === 'REJECT_AND_REFILL') && (
                <Button variant="primary" onClick={() => handleRefillRefund(selectedOrder)}>Refill Refund Form</Button>
              )}
              <Button variant="secondary" onClick={() => setShowOrderModal(false)}>Close</Button>
            </div>
          </div>
        </div>
      )}
      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </Layout>
  );
};

export default BuyerOrders;
